import React, { useState, useCallback } from "react";
import { DataElement, DataElementView, DataSource, SourceFieldMapping } from "@/entities/all";
import { PlusCircle, ArrowDownCircle, ArrowUpCircle, Blocks } from "lucide-react";

import ElementsTable from "../components/elements/ElementsTable";
import NewElementModal from "../components/elements/NewElementModal";
import { DictionaryImportDialog } from "../components/dictionary-import/DictionaryImportDialog";
import { exportDataElements } from "../utils/excelTemplateGenerator";
import PaginatedTable from "../components/ui/paginated-table";
import { FilterSelect, FilterSelectItem } from "@/components/ui/filter-select";

export default function DataElements() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ status: 'all', dataType: 'all', dataSource: 'all' });
  const [isNewElementModalOpen, setIsNewElementModalOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [total, setTotal] = useState(0);
  const [dataSources, setDataSources] = useState([]);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Load data sources for filter once
  React.useEffect(() => {
    DataSource.list('source_name').then(s => setDataSources(s.map(x => x.source_name).filter(Boolean))).catch(() => {});
  }, []);

  const loadData = useCallback(async (page, pageSize) => {
    const offset = (page - 1) * pageSize;
    let dataSourceElementIds = null;
    if (filters.dataSource !== 'all') {
      try {
        const sources = await DataSource.filter({ source_name: filters.dataSource });
        if (sources.length > 0) {
          const mappings = await SourceFieldMapping.filter({ source_id: sources[0].id });
          dataSourceElementIds = new Set(mappings.map(m => m.canonical_element_id));
        } else {
          dataSourceElementIds = new Set();
        }
      } catch {
        dataSourceElementIds = new Set();
      }
    }

    const serverFilters = {};
    if (filters.status !== 'all') serverFilters.status = filters.status;
    if (filters.dataType !== 'all') serverFilters.data_type = filters.dataType;

    const hasFilters = Object.keys(serverFilters).length > 0 || searchTerm;
    const searchColumns = ['canonical_name', 'definition'];
    const loadLimit = dataSourceElementIds ? 0 : pageSize;
    const loadOffset = dataSourceElementIds ? 0 : offset;

    let response;
    if (hasFilters) {
      response = await DataElementView.filterWithTotal(serverFilters, 'canonical_name', loadLimit, loadOffset, searchTerm, searchColumns);
    } else {
      response = await DataElementView.listWithTotal('canonical_name', loadLimit, loadOffset);
    }

    let mappedElements = (response.data || []).map(el => ({
      ...el,
      status: el.status || 'DRAFT',
      definition: el.definition || '',
      data_type: el.data_type || '',
      version_number: el.current_version_number || 1,
    }));

    if (dataSourceElementIds) {
      mappedElements = mappedElements.filter(el => dataSourceElementIds.has(el.id));
      const t = mappedElements.length;
      mappedElements = mappedElements.slice(offset, offset + pageSize);
      setTotal(t);
      return { data: mappedElements, total: t };
    }

    setTotal(response.total || 0);
    return { data: mappedElements, total: response.total || 0 };
  }, [filters, searchTerm]);

  const triggerReload = () => setReloadTrigger(prev => prev + 1);

  const handleElementUpdated = () => { triggerReload(); setIsNewElementModalOpen(false); };
  const handleImportComplete = () => triggerReload();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const allElements = await DataElementView.list('canonical_name');
      exportDataElements(allElements);
    } catch (error) {
      console.error('Error exporting elements:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteElement = async (elementId) => {
    try {
      await DataElement.delete(elementId);
      triggerReload();
    } catch (error) {
      console.error('Error deleting element:', error);
      throw error;
    }
  };

  return (
    <div data-tour="page-data-elements" className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Title + Actions */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Data Elements</h1>
          <p className="text-sm font-normal text-page-muted">Manage and maintain your canonical data elements</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <button
            onClick={() => setIsImportDialogOpen(true)}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <ArrowUpCircle size={24} />
            Import
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || total === 0}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownCircle size={24} />
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
          <button
            onClick={() => setIsNewElementModalOpen(true)}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <PlusCircle size={24} />
            New Element
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] flex items-center h-9 bg-card-bg rounded-[18px] px-3 gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <path d="M21 21L17.5001 17.5M20 11.5C20 16.1944 16.1944 20 11.5 20C6.80558 20 3 16.1944 3 11.5C3 6.80558 6.80558 3 11.5 3C16.1944 3 20 6.80558 20 11.5Z" stroke="#377AD6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            placeholder="Search elements by name or definition..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm text-page-text placeholder:text-page-muted"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0 max-w-full">
          <FilterSelect value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))} placeholder="Status">
            <FilterSelectItem value="all">All Status</FilterSelectItem>
            <FilterSelectItem value="APPROVED">Approved</FilterSelectItem>
            <FilterSelectItem value="DRAFT">Draft</FilterSelectItem>
            <FilterSelectItem value="PENDING_REVIEW">Pending Review</FilterSelectItem>
            <FilterSelectItem value="DEPRECATED">Deprecated</FilterSelectItem>
          </FilterSelect>
          <FilterSelect value={filters.dataType} onValueChange={(v) => setFilters(f => ({ ...f, dataType: v }))} placeholder="Data Type">
            <FilterSelectItem value="all">All Types</FilterSelectItem>
            {['string','large_string','int8','int16','int32','int64','uint8','uint16','uint32','uint64','float16','float32','float64','bool','date32','time32','time64','timestamp','duration','decimal128','uuid','binary','large_binary'].map(t => (
              <FilterSelectItem key={t} value={t}>{t}</FilterSelectItem>
            ))}
          </FilterSelect>
          <FilterSelect value={filters.dataSource} onValueChange={(v) => setFilters(f => ({ ...f, dataSource: v }))} placeholder="Data Source">
            <FilterSelectItem value="all">All Sources</FilterSelectItem>
            {dataSources.map(s => (
              <FilterSelectItem key={s} value={s}>{s}</FilterSelectItem>
            ))}
          </FilterSelect>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-2">
        <Blocks size={24} color="#377AD6" />
        <span className="text-sm font-medium text-[#377AD6]">{total.toLocaleString()} element{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <PaginatedTable
        loadData={loadData}
        dependencies={[filters, searchTerm, reloadTrigger]}
        renderTable={(paginatedData, loading) => (
          <div className="bg-card-bg rounded-[8px] overflow-x-auto">
            <ElementsTable
              elements={paginatedData}
              isLoading={loading}
              onDeleteElement={handleDeleteElement}
            />
          </div>
        )}
      />

      <NewElementModal
        isOpen={isNewElementModalOpen}
        onClose={() => setIsNewElementModalOpen(false)}
        onElementCreated={handleElementUpdated}
      />

      <DictionaryImportDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
}
